import { createAsyncThunk } from "@reduxjs/toolkit";

import {
    getMenus as getMenusApi,
    getMenuCategories as getMenuCategoriesApi,
    createMenuCategory as createMenuCategoryApi,
    updateMenuCategory as updateMenuCategoryApi,
    deleteMenuCategory as deleteMenuCategoryApi,
    reorderMenuCategories as reorderMenuCategoriesApi,
    updateMenu as updateMenuApi,
    updateMenuStatus as updateMenuStatusApi,
    deleteMenu as deleteMenuApi,
} from "../../helpers/backend_helper";

export interface GetMenusParams {
    [key: string]: unknown;
}

export interface GetMenuCategoriesParams {
    [key: string]: unknown;
}

export interface CreateMenuCategoryPayload {
    [key: string]: unknown;
}

export interface UpdateMenuCategoryPayload {
    categoryId: string | number;
    data: Record<string, unknown>;
}

export interface ReorderMenuCategoriesPayload {
    categoryIds: Array<string | number>;
}

export interface UpdateMenuPayload {
    menuId: string | number;
    data: Record<string, unknown>;
}

export interface ToggleMenuAvailabilityPayload {
    menuId: string | number;
    available: boolean;
}

export const getMenus = createAsyncThunk(
    "Menu/getMenus",
    async (_: void, { rejectWithValue }) => {
        try {
            const response = await getMenusApi();
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getMenuCategories = createAsyncThunk(
    "Menu/getMenuCategories",
    async (_: void, { rejectWithValue }) => {
        try {
            const response = await getMenuCategoriesApi();
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const createMenuCategory = createAsyncThunk(
    "Menu/createMenuCategory",
    async (data: CreateMenuCategoryPayload, { rejectWithValue }) => {
        try {
            const response = await createMenuCategoryApi(data);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updateMenuCategory = createAsyncThunk(
    "Menu/updateMenuCategory",
    async ({ categoryId, data }: UpdateMenuCategoryPayload, { rejectWithValue }) => {
        try {
            const response = await updateMenuCategoryApi(categoryId, data);
            return {
                categoryId,
                response,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const reorderMenuCategories = createAsyncThunk(
    "Menu/reorderMenuCategories",
    async ({ categoryIds }: ReorderMenuCategoriesPayload, { rejectWithValue }) => {
        try {
            const response = await reorderMenuCategoriesApi(categoryIds);
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const deleteMenuCategory = createAsyncThunk(
    "Menu/deleteMenuCategory",
    async (categoryId: string | number, { rejectWithValue }) => {
        try {
            const response = await deleteMenuCategoryApi(categoryId);
            return {
                categoryId,
                response,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updateMenu = createAsyncThunk(
    "Menu/updateMenu",
    async ({ menuId, data }: UpdateMenuPayload, { rejectWithValue }) => {
        try {
            const response = await updateMenuApi(menuId, data);
            return {
                menuId,
                request: data,
                response,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const deleteMenu = createAsyncThunk(
    "Menu/deleteMenu",
    async (menuId: string | number, { rejectWithValue }) => {
        try {
            const response = await deleteMenuApi(menuId);
            return {
                menuId,
                response,
            };
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const toggleMenuAvailability = createAsyncThunk(
    "Menu/toggleMenuAvailability",
    async ({ menuId, available }: ToggleMenuAvailabilityPayload, { rejectWithValue }) => {
        const requestPayload = {
            available,
            isAvailable: available,
            availability: available ? "available" : "unavailable",
            status: available ? "available" : "unavailable",
        };

        try {
            try {
                const response = await updateMenuStatusApi(menuId, requestPayload);
                return {
                    menuId,
                    request: requestPayload,
                    response,
                };
            } catch (statusError) {
                const fallbackResponse = await updateMenuApi(menuId, requestPayload);
                return {
                    menuId,
                    request: requestPayload,
                    response: fallbackResponse,
                };
            }
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
